// 能力类型
export enum AbilityType {
  advertisingAbility = 'delivery_ability_score',
  creativeAbility = 'creative_ability_score',
  operateAbility = 'operate_ability_score',
  riskManageAbility = 'risk_manage_score',
  riskAbility = 'risk_ability_score',
  professionalCertification = 'professional_certification_score',
  techDataAbility = 'tech_ability_score',
  strategyAbility = 'strategy_score',
}

// 赛道类型
export enum TrackType {
  biddingOperation = 'BID',
  biddingOperationK2 = 'BID__K2',
  biddingOperationK3 = 'BID__K3',
  biddingOperationK1 = 'BID_K1', // K1赛道与K2、K3指标不同，不复用BID配置
  ecommerceAd = 'E_COMMERCE',
  ecommerceAdBrand = 'E_COMMERCE__BRAND',
  ecommerceAdBulk = 'E_COMMERCE__BULK',
  regionService = 'REGIONAL',
  globalSearch = 'SEARCH',
  globalSearchZone = 'SEARCH__ZONE',
  globalSearchKa = 'SEARCH__KA',
  brandMarketing = 'BRAND',
  brandMarketingContract = 'BRAND_CONTRACT',
  brandMarketingInvestment = 'BRAND_INVESTMENT',
  verticalBusiness = 'VERTICAL',
  verticalBusinessOperator = 'VERTICAL__OPERATOR',
  verticalBusinessMedical = 'VERTICAL__MEDICAL',
  verticalBusinessMedicalDevice = 'VERTICAL__MEDICAL_DEVICE',
  verticalBusinessEstate = 'VERTICAL__ESTATE',
  verticalBusinessRegionalEstate = 'VERTICAL__REGIONAL_ESTATE',
  verticalBusinessCar = 'VERTICAL__CAR',
  verticalBusinessK5 = 'VERTICAL__K5',
  verticalBusinessOversea = 'VERTICAL__OVERSEA',
  marketingScience = 'MARKETING_SCIENCE',
  techService = 'TECHNICAL',
  techServiceApi = 'TECHNICAL__API',
  techServiceEcomm = 'TECHNICAL__MINI',
  techServiceMcn = 'TECHNICAL__MCN',
  masterMarketing = 'INFLUENCER_MARKETING',
}

export enum ProvinceType {
  s = 's',
  a = 'a',
  b = 'b',
}

export enum AgencyRangeType {
  all = 0,
  self = 1,
}

// 赛道模型
export interface TrackItem {
  key: string;
  name: string;
  enable?: boolean; // 是否开启，根据接口返回计算
  disabled?: boolean; // 是否禁用，可配置
  force?: boolean; // 是否强制开启，可配置
  dynamic?: boolean; // 是否动态加载子赛道数据
  display?: string; // 是否展示规则
  isIncentive?: boolean; // 是否激励
  featureFlag?: string; // 灰度开关
  data?: Array<{
    key: string;
    name: string;
    dynamic?: boolean;
    display?: string; // 是否展示规则
    isIncentive?: boolean; // 是否激励
    data?: Array<{
      key: string;
      name: string;
    }>
  }>
}

// 能力模型维度（梯队）
export interface AbilityModelDimen {
  key: string;
  name: string;
  color: string;
  main?: boolean; // 是否主要维度（自身得分）
  score?: number; // 梯队得分标准
  display?: string; // 是否展示规则
}

// 能力模型
export interface AbilityModelItem {
  key: string;
  dataKey: string; // 从接口取值字段
  name: string;
  max: number; // 最大值
  indicator: AbilityIndicator[]; // 指标
  abilityModelDimen?: Array<AbilityModelDimen>; // 对比特定维度，如专业认证
  indicatorDimen?: Array<AbilityModelDimen>; // 指标对比特定维度，如专业认证，用于指标值对比，radar、progress、AbilityTable中使用
  tips?: string; // 说明
  tipsProps?: {
    type: 'hover' | 'dialog'; // 说明类型
    label: any; // 按钮文案
  };
  hide?: boolean; // 是否隐藏，如竞价风控
  summaryType?: 'radar' | 'progress'; // 汇总组件类型
  summaryDesc?: string; // 汇总底部描述
  summaryDescTips?: string; // 汇总底部描述详细提示
  radarKey?: string; // 指标值key兼容接口格式，radar图中用，优先级高于key
  showAgencyRangeSwitch?: boolean; // 是否切换全量/一代范围
  useAgencyRangeParam?: boolean; // 是否使用一代范围参数，showAgencyRangeSwitch为true时默认使用
  hasReference?: boolean; // progress图中是否展示参考值
  detailTitle?: string; // 详情drawer标题
  hasDetail?: boolean; // 是否有详情页面
  columnTitle?: string; // AbilityListview表格标题
  columnKey?: string; // AbilityListview表格列wuji key
  columnMap?: Record<string, any>; // AbilityListview表格列wuji map
  showRiskBoard?: boolean; // 是否展示风险能力看板
  showRiskDetail?: boolean; // 是否展示风险能力详情(重点关注中显示详情按钮)
  riskScoreFixed?: boolean; // 风险管理满分是否固定(25)，不固定时取max为最大值
  display?: string; // 是否展示规则
  [period: string]: any; // 不同年份配置，覆盖以上配置（建议改动较小时使用，改动较大时使用display）
}

export type AbilityModel = AbilityModelItem & {
  color: string;
  colorEnd: string
};

// 能力指标
export interface AbilityIndicator {
  key: string;
  name: string;
  max: number;
  tips?: string;
  desc?: string;
  disabled?: boolean;
  showCumulation?: boolean; // 在AbilityTable组件中左侧展示累计图，不配置默认雷达图
  progressKey?: string; // 指标值key兼容接口格式，progress图中用，优先级高于key
  useAbilityScore?: boolean; // 是否使用能力分计算进度，progress图中用
  isWeekPartition?: boolean; // 是否按周分区，如投放能能力、创意能力原能力模型指标
  showReference?: boolean; // progress图中是否展示参考值
  // 数值相关
  valueConfig?: {
    key?: string;
    name?: string;
    max?: number; // 最大值
    maxKey?: string; // 存放最大值的key，用于服务商最大值不一样时
    threshold?: number; // 阈值，kpiProgress中用作最低门槛值
    unit?: string;
    precision?: number;
    countKey?: string; // 数值分子key，用于unit=percent时
    allCountKey?: string; // 数值分母key，用于unit=percent时
    gapKey?: string; // 数值差距key
    [key: string]: any; // 其他部分指标自定义
  };
  // 提升建议相关
  suggestion?: {
    key?: string; // 读取建议的字段
    belowTypeKey?: string; // AbilityListview中belowTypeConfig中的key，优先级suggestion.belowTypeKey > suggestion.key > key
    name?: string; // 展示在建议中的名称
    dimen?: string; // 客户｜行业 或其他
    displayCnt?: number; // 展示几个客户/行业
    summaryTemplate?: string; // 自定义模板-汇总
    detailTemplate?: string; // 自定义模板-详情
    postfix?: string; // 自定义后缀，目前仅用于kpiProgress情况
    listIndex?: number; // 提升建议联动二级页面表格时，表格的序号
  };
  // kpi进度相关
  kpiConfig?: {
    key: string; // 读取kpi的key
    name: string; // 展示在kpi进度中的名称
    unit: string; // 单位
    precision?: number; // 精度
    rangeName?: string; // 区间名称
    // target在外层时，直接作为目标计算，优先级高于rangeList
    target?: number | Array<{
      key: string;
      max: number;
    }>;
    rangeList: Array<{
      min?: number;
      max?: number;
      name: string;
      target: number | Array<{
        key: string;
        max: number;
      }>;
    }>;
    [key: string]: any; // target指标名称映射
  };
  // 子指标
  list?: Array<{
    key: string;
    name: string;
    max?: number;
    threshold?: number;
    thresholdKey?: string;
    showInDetail?: boolean;
    tips?: string;
    unit?: string;
    precision?: number;
    display?: string; // 是否展示规则
  }>;
  detailDesc?: string; // 详情内容，不通用，特殊组件使用，如aigc提报
  category?: string[]; // 所属岗位，人才认证类型专用
  display?: string; // 是否展示规则
  [period: string]: any; // 不同年份配置，覆盖以上配置（建议改动较小时使用，改动较大时使用display）
}

// 比较指标
export interface CompareStatistic {
  key: string;
  name: string;
  unit?: string;
}


// 重点关注
export interface KeyEventItem {
  key: string;
  title: string;
  desc: string;
  type: 'detail' | 'link' | 'dialog' | 'detailPage';
  value?: string;
  link?: string;
  buttonText?: string;
  buttonProps?: Record<string, any>;
  props?: Record<string, any>;
  tracks?: string[];
  reportKey?: string; // 自定义上报key
  isNew?: boolean; // 是否新信息
  // 引导
  guide?: {
    title: string;
    content: string;
    button?: string;
    buttonProps?: Record<string, any>;
  };
  display?: string;
}

export interface KeyEvents {
  count: number;
  forceList?: KeyEventItem[]; // 强制展示的事件（在前面）
  list: KeyEventItem[];
}

export interface RankListItem {
  key: string;
  name: string;
  tips?: string;
  showAgencyRangeSwitch?: string;
  display?: string;
}

// 工具箱
export interface CommonToolItem {
  key: string;
  label: string;
  desc: string;
  link: string;
  icon: string;
}

// 各赛道配置
export interface TrackConfig {
  abilityModelDimen: Array<AbilityModelDimen>;
  abilityModel: Array<AbilityModelItem>;
  rankList?: Array<RankListItem>;
  rankTitle?: string; // 排行榜标题
  scoreRulesTitle: string; // 得分规则标题
  scoreRules: string; // 得分规则内容
  scoreRulesTemplate?: Record<string, string>; // 得分规则模板变量
  kpiProgress?: boolean; // 没有打分，只展示KPI进度， 如垂直K5、封闭汽车
  AIAssistantType?: 'weed' | 'month'; // AI周报类型
  [key: string]: any; // 其他配置
}

// 成长中心配置
export interface GrowupConfig {
  enableFreeLogin: boolean; // 免登录是否可用（针对免登录禁用的功能，如列表下载）
  incentiveGuide?: string; // 激励中心引导文案
  periodConfig: {
    startQuarter: string; // 起始季度
    newQuarterEnableDate: number; // 新季度开始启用日期
    quarterFrozenDate: number; // 季度结束后几天结算
    frozenTips?: string; // 已结算提示
    data: Array<{
      value: string;
      label: string;
      preview?: boolean; // 是否预览（只展示评分规则）,track_list请求前一季度
      partTrackPreview?: boolean; // 是否部分赛道预览（只展示评分规则），track_list请求当前季度
      [key: string]: any;
    }>;
  };
  previewTrackList?: string[]; // 预览赛道列表
  trackModelList: TrackItem[]; // 赛道
  trackPermMap: Record<TrackType, TrackType>; // 赛道权限映射A->B，有A赛道权限时就有B赛道权限
  colorConfig: Array<{ color: string; colorEnd: string }>; // 颜色配置
  abilityModelDimenMap: Record<string, AbilityModelDimen>; // 能力模型维度映射，以便复用
  indicatorDimenMap: Record<string, AbilityModelDimen>; // 指标维度，用于指标值对比，radar、progress、AbilityTable中使用
  abilityModelMap: Record<string, AbilityModel>; // 能力模型映射，以便复用
  indicatorMap: Record<string, AbilityIndicator>; // 能力指标映射，以便复用
  trackConfig: Record<TrackType, TrackConfig>;
  compareStatistic: CompareStatistic[];
  illegalTrendModel: Array<{ key: string; name: string }>; // 风险趋势模型
  keyEvents: KeyEvents;
  commonTools: CommonToolItem[];
  trackCommonConfig: Partial<TrackConfig>; // 赛道公共配置,主要用于scoreRulesTemplate复用
  trackNotice?: Record<string, string | Record<string, string>>; // 赛道通知, 赛道: 通知文案 | { 季度: 通知文案 }
}

// 预警
export interface AbilityAlarm {
  message: string; // 内容
  alarm: boolean; // 是否预警
  name: string;
  key: string;
  score: number;
}
